class MenuDrawerGuard {
  constructor() {
    this.hiddenFloatingWidgets = [];
    this.isSuppressingUi = false;
    this.boundSync = this.sync.bind(this);
    this.boundHandleMutations = this.handleMutations.bind(this);
  }

  init() {
    if (this.initialized) return;
    this.initialized = true;

    this.bodyObserver = new MutationObserver(this.boundHandleMutations);
    this.bodyObserver.observe(document.body, {
      attributes: true,
      attributeFilter: ['class'],
      childList: true,
      subtree: true,
    });

    document.addEventListener('click', () => window.setTimeout(this.boundSync, 0));
    window.addEventListener('resize', this.boundSync);
    window.addEventListener('orientationchange', this.boundSync);

    this.sync();
  }

  handleMutations(mutations) {
    const shouldSync = mutations.some(
      (mutation) =>
        mutation.type === 'attributes' ||
        mutation.addedNodes.length > 0 ||
        mutation.removedNodes.length > 0
    );

    if (shouldSync) this.sync();
  }

  isMenuDrawerOpen() {
    if (window.innerWidth > 989) return false;

    const bodyLocked =
      document.body.classList.contains('overflow-hidden-mobile') ||
      document.body.classList.contains('overflow-hidden-tablet');

    const openHeaderDrawer = document.querySelector('header-drawer details[open]');

    return Boolean(bodyLocked && openHeaderDrawer);
  }

  sync() {
    const isOpen = this.isMenuDrawerOpen();

    document.body.classList.toggle('menu-drawer-open', isOpen);

    if (isOpen) {
      this.hideFloatingWidgets();
      return;
    }

    this.restoreFloatingWidgets();
  }

  hideFloatingWidgets() {
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const nodes = Array.from(document.body.querySelectorAll('*'));
    const hiddenNodes = new Set(this.hiddenFloatingWidgets.map(({ node }) => node));

    const newlyHiddenWidgets = nodes
      .filter((node) => {
        if (!(node instanceof HTMLElement)) return false;
        if (hiddenNodes.has(node)) return false;
        if (node.closest('header-drawer, menu-drawer, cart-drawer, predictive-search, modal-dialog')) return false;

        const style = window.getComputedStyle(node);
        if (style.position !== 'fixed' || style.display === 'none' || style.visibility === 'hidden') return false;

        const rect = node.getBoundingClientRect();
        if (rect.width === 0 || rect.height === 0) return false;

        const isEdgePinned =
          rect.right >= viewportWidth - 40 ||
          rect.left <= 40 ||
          rect.bottom >= viewportHeight - 40 ||
          rect.top <= 40;

        const isFloatingUi =
          rect.width <= Math.min(viewportWidth - 24, 720) &&
          rect.height <= Math.min(viewportHeight - 24, 720);

        return isEdgePinned && isFloatingUi;
      })
      .map((node) => {
        const previous = {
          node,
          pointerEvents: node.style.pointerEvents,
          opacity: node.style.opacity,
          visibility: node.style.visibility,
        };

        node.style.pointerEvents = 'none';
        node.style.opacity = '0';
        node.style.visibility = 'hidden';

        return previous;
      });

    if (newlyHiddenWidgets.length > 0) {
      this.hiddenFloatingWidgets = [...this.hiddenFloatingWidgets, ...newlyHiddenWidgets];
    }

    if (this.hiddenFloatingWidgets.length > 0) {
      this.isSuppressingUi = true;
    }
  }

  restoreFloatingWidgets() {
    if (!this.isSuppressingUi) return;

    this.hiddenFloatingWidgets.forEach(({ node, pointerEvents, opacity, visibility }) => {
      if (!(node instanceof HTMLElement)) return;
      node.style.pointerEvents = pointerEvents;
      node.style.opacity = opacity;
      node.style.visibility = visibility;
    });

    this.hiddenFloatingWidgets = [];
    this.isSuppressingUi = false;
  }
}

if (!window.menuDrawerGuard) {
  window.menuDrawerGuard = new MenuDrawerGuard();
  window.menuDrawerGuard.init();
}
